<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);

// Single items with a route: use router-link. Expandable groups: use button.
const isLink = computed(() => !!props.to && !props.expandable);

function handleClick() {
  if (props.expandable) {
    emit('toggle');
  }
}
</script>

<template>
  <component
    :is="isLink ? 'router-link' : 'button'"
    :type="isLink ? undefined : 'button'"
    :to="isLink ? to : undefined"
    class="flex w-full items-center gap-2 rounded-[10px] px-2.5 py-2 text-left text-sm font-medium transition-all hover:bg-n-alpha-2 hover:text-n-slate-12"
    :class="{
      'bg-pz-brand-tint text-pz-brand-on-tint font-semibold': isActive,
      'text-n-slate-12': hasActiveChild && !isActive,
    }"
    @click="handleClick"
  >
    <div v-if="icon" class="relative flex items-center shrink-0">
      <Icon :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-0.5 ltr:-right-0.5 rtl:-left-0.5 bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <span class="flex-1 truncate">{{ label }}</span>
    <span
      v-if="dynamicCount && !expandable"
      class="rounded-md text-xs leading-5 font-medium text-center border px-1.5 shrink-0"
      :class="{
        'border-sidebar-border/60': isActive,
        'border-transparent': !isActive,
      }"
      >{{ count }}</span>
    <span
      v-if="expandable"
      class="i-lucide-chevron-right size-4 shrink-0 transition-transform duration-200"
      :class="{ 'rotate-90': isExpanded }"
    />
  </component>
</template>
